import React, { useEffect } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';

import belowIcon from '../../../asset/icon/below.png';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getPageMessage } from '../../../shared/constants/pageMessages';
import { SelectedData, WorkSimpleWithDetails } from '../../../shared/dto/EntityRepository';
import { AlertPosition, AlertType, ProjectElementType } from '../../../shared/enum/EnumRepository';
import { useProjectDetailState } from '../../../shared/hooks/useApi/useProjectDetail';
import { useProjectElementActions, useProjectElementState } from '../../../shared/hooks/useApi/useProjectElement';
import { useCanShowProjectDetail } from '../../../shared/hooks/useProjectVisibility';
import { useImportStationWorkStore } from '../../../shared/store/importStationWorkStore';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useModalWorkTagStore } from '../../../shared/store/workTagStore';
import { useModalPageStore } from '../../../shared/store/pageStore';

import ImportTagStation from '../ImportTagStation';
import PageAboutWorkImport from '../PageAboutWorkImport';
import SelectedDataThumb from '../SelectedDataThumb';
import SimpleWorkForPE from '../SimpleWorkForPE';

const ImportStationWorkMobile: React.FC = () => {
  const { clearModal } = useModalStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();
  const { selectedDataList, clearSelectedDataList, clearSimpleList, simpleList } = useImportStationWorkStore();
  const { projectElementList } = useProjectElementState();
  const { importWorkOrDetailAsPeBulk } = useProjectElementActions();
  const { enqueueAlert } = useStandardAlertStore();
  const { selectedModalTags, clearSelectedModalTags } = useModalWorkTagStore();
  const resetModalPage = useModalPageStore((s) => s.resetModalPage);

  const clearImportModalState = () => {
    clearSelectedDataList();
    clearSimpleList();
    clearSelectedModalTags();
    resetModalPage();
  };

  useEffect(() => () => {
    clearImportModalState();
  }, []);

  const handleSubmit = async () => {
    if (!project) return;
    if (selectedDataList.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    const workIdsInProject = new Set(
      projectElementList
        .filter((pe) => pe.projectElementType === ProjectElementType.WORK)
        .map((pe) => (pe as { work: { id: string } }).work.id)
    );
    const detailIdsInProject = new Set(
      projectElementList
        .filter((pe) => pe.projectElementType === ProjectElementType.DETAIL)
        .map((pe) => (pe as { workDetail: { id: string } }).workDetail.id)
    );
    const workIdList = selectedDataList
      .filter((item) => item.detailId == null && !workIdsInProject.has(item.workId))
      .map((item) => item.workId);
    const workDetailIdList = selectedDataList
      .filter((item) => item.detailId != null && !detailIdsInProject.has(item.detailId!))
      .map((item) => item.detailId!);
    if (workIdList.length === 0 && workDetailIdList.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    await importWorkOrDetailAsPeBulk({
      projectId: project.id,
      workIdList,
      workDetailIdList,
    });
  };

  const handleClose = () => {
    clearImportModalState();
    clearModal(400);
  };

  if (!canShowProject || !project) return null;

  return (
    <ImportComp>
        <TitleWrapper>
          <TitleContainer>
            <Title>{getModalMessage('SELECT_TO_IMPORT_WORK_DETAIL')}</Title>         
          </TitleContainer>
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent={true}
            backgroundImg={belowIcon}
            onClick={handleClose}
            aria-label="Close"
          />
        </TitleWrapper>
        <SelectedDataContainer>
          {selectedDataList.length === 0 ? (
            <SelectedDataPlaceholder>
              {selectedModalTags.length > 0 && simpleList.length === 0
                ? getPageMessage('IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS')
                : getModalMessage('SELECT_TO_IMPORT_EMPTY')}
            </SelectedDataPlaceholder>
          ) : (
            selectedDataList.map((item: SelectedData, i: number) => (
              <SelectedDataThumb
                key={`${item.workId}-${item.detailId ?? 'work'}-${i}`}
                item={item}
              />
            ))
          )}
        </SelectedDataContainer>
        <ImportTagStation />
        <ImportContentContainer>
          {simpleList.length === 0 ? (
            <NoContentContainer>
              {selectedModalTags.length > 0
                ? getPageMessage('IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS')
                : getPageMessage('IMPORT_STATION_EMPTY_WORK')}
            </NoContentContainer>
          ) : (
            simpleList.map((sw: WorkSimpleWithDetails) => (
              <SimpleContentComp key={sw.id}>
                <SimpleWorkForPE data={sw} />
              </SimpleContentComp>
            ))
          )}
        </ImportContentContainer>
        <PageAboutWorkImport />
        <HeadlessBtn
          value={getBtnName('IMPORT')}
          handleClick={handleSubmit}
          StyledBtn={SubmitButton}
        />
    </ImportComp>
  );
};

const ImportComp = styled.div`
  width: 100%;
  height: 84vh;
  min-height: 84vh;
  display: flex;
  flex-direction: column;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_01};
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;


const SelectedDataContainer = styled.div`
  height: 12vh;
  min-height: 12vh;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 10px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SelectedDataPlaceholder = styled.div`
  min-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const ImportContentContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NoContentContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_2};
  white-space: pre-line;
`;

const SimpleContentComp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 15px 0 10px 0;
`;

export default ImportStationWorkMobile;
